/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365 from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAccountlinksData, IHeaderProps, IStaticlinksData } from '../definition-extensions/header.ext.props.autogenerated';
import getSalesRepInformationAsync, { GetSalesRepInfoInput } from '../../../actions/get-sales-rep-information.action';
import { ICustomerSalesRepEntity } from '../../../actions/DataServiceEntities.g';
import { useEffect, useState } from 'react';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
const HeaderView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = (props) => {
    const menuNode = React.useRef<HTMLDivElement>(null);
    const [salesRepData, setSalesRepData] = useState<ICustomerSalesRepEntity>();

    useEffect(() => {
        const fetchSalesRepData = async () => {
            if (props.config.showContactSalesRep && props.context.request.user?.isAuthenticated && props.context.request.user?.customerAccountNumber) {
                const input: GetSalesRepInfoInput = new GetSalesRepInfoInput(
                    props.context.request.user?.customerAccountNumber,
                                props.context.request.apiSettings
                            );
                const contactSalesRep = await getSalesRepInformationAsync( input,  props.context.actionContext );
                if (contactSalesRep) {
                    setSalesRepData(contactSalesRep);
                }
            }
        };
        fetchSalesRepData();

        return () => undefined; // Explicitly return undefined to satisfy all code paths
    }, [props.config.showContactSalesRep, props.context.request.user]);

    useEffect(() => {
        if (props.config.useStickyHeader) {
            const configureStickyHeader = () => {
                const defaultValue = 0;
                const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
                const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

                const headerElement = document.querySelector('header .default-container');
                if (headerElement) {
                    if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                        headerElement.classList.add('lock-opaque');
                        headerElement.classList.add('fixed');
                    } else {
                        headerElement.classList.remove('lock-opaque');
                        headerElement.classList.remove('fixed');
                    }
                }

                const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
                const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

                if (stickyHeader && headerLogo) {
                    const navStickyPos = headerLogo.offsetHeight + bannerHeights;
                    if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                        stickyHeader.classList.add('fixed');
                    } else {
                        stickyHeader.classList.remove('fixed');
                    }
                }
            };

            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', configureStickyHeader);
            }
            configureStickyHeader();

            return () => {
                if (MsDyn365.isBrowser) {
                    window.removeEventListener('scroll', configureStickyHeader);
                }
            };
        }
        return () => undefined;
    }, [props.config.useStickyHeader]);

    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        Divider,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer
    } = props;

    const _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                        return <>{slot}</>;
                    })
                    : null}
            </>
        );
    };

    function _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = props;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    const _renderStaticHeaderLinks = (links: IStaticlinksData[]): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        return (
            <>
                {links ? (
                    links.map((link: IStaticlinksData) => {
                        return (
                            <a className='msc-btn header-link' href={link.linkUrl.destinationUrl}>
                                {link.linkText}
                            </a>
                        );
                    })
                ) : (
                    <></>
                )}
            </>
        );
    };

    function _renderSalesRepInformation(
        salesRepData: ICustomerSalesRepEntity | undefined,
        props: Readonly<IHeaderViewProps & IHeaderProps<{}>> & Readonly<{ children?: React.ReactNode; }>
    ): JSX.Element | null {
        const [visible, setVisible] = useState(false);
        const [shouldStayOpen, setShouldStayOpen] = useState(false);
        
        const showPopup = () => {
            setVisible(true);
        };

        const hidePopupWithDelay = () => {
            window.setTimeout(() => {
                if (!shouldStayOpen) {
                    setVisible(false);
                }
            }, 500); // 300ms delay
        };

        const handlePopupEnter = () => {
            setShouldStayOpen(true);
        };

        const handlePopupLeave = () => {
            setShouldStayOpen(false);
            hidePopupWithDelay();
        };

        return (
            <div className="relative inline-block">
                <a
                    href="#"
                    onMouseEnter={showPopup}
                    onMouseLeave={hidePopupWithDelay}
                    className="msc-btn header-link"
                >
                    Contact
                </a>

                {visible && (
                    <div
                        className="header-popup"
                        onMouseEnter={handlePopupEnter}
                        onMouseLeave={handlePopupLeave}
                    >
                        <div className="header-popup-content">
                            <a onClick={() => { setVisible(false); }} className='close-popup-btn'>X</a>
                            <div className="header-popup-section">
                                <div className="header-popup-title">Main:</div>
                                <div className="header-popup-line">
                                    <span className="header-popup-label">Phone: </span>
                                    <span className="header-popup-text">{props.config.contactSalesRepPhone}</span>
                                </div>
                                <div className="header-popup-line">
                                    <span className="header-popup-label">Email: </span>
                                    <span className="header-popup-text">{props.config.contactSalesRepEmail}</span>
                                </div>
                                <div className="header-popup-line">
                                    <span className="header-popup-label">Website: </span>
                                    <span className="header-popup-text">{props.config.contactSalesRepWebsite}</span>
                                </div>
                            </div>
                            {salesRepData ? (
                                <>
                                    <div className="header-popup-section">
                                        <div className="header-popup-title">Outside Sales Rep:</div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Name: </span>
                                            <span className="header-popup-text">{`${salesRepData.OutSaleRepFirstName} ${salesRepData.OutSalesRepLastName}`}</span>
                                        </div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Email: </span>
                                            <span className="header-popup-text">{salesRepData.OutSalesRepEmail}</span>
                                        </div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Phone: </span>
                                            <span className="header-popup-text">{salesRepData.OutSalesRepPhone}</span>
                                        </div>
                                    </div>
                                    <div className="header-popup-section">
                                        <div className="header-popup-title">Inside Sales Rep:</div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Name: </span>
                                            <span className="header-popup-text">{`${salesRepData.InSaleRepFirstName} ${salesRepData.InSalesRepLastName}`}</span>
                                        </div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Email: </span>
                                            <span className="header-popup-text">{salesRepData.InSalesRepEmail}</span>
                                        </div>
                                        <div className="header-popup-line">
                                            <span className="header-popup-label">Phone: </span>
                                            <span className="header-popup-text">{salesRepData.InSalesRepPhone}</span>
                                        </div>
                                    </div>
                                </>) : null}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const _renderAccountHeaderLinks = (props: IHeaderViewProps, links: IAccountlinksData[]): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        if (props.context.request.user.isAuthenticated) {
            return (
                <>
                    {links ? (
                        links.map((link: IAccountlinksData) => {
                            return (
                                <a className='msc-btn header-link' href={link.linkUrl.destinationUrl}>
                                    {link.linkText}
                                </a>
                            );
                        })
                    ) : (
                        <></>
                    )}
                </>
            );
        }
        return null;
    };

    const _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // slint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signInLink
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                const _renderAccountDropdownLinks = (props: IHeaderViewProps): JSX.Element | null => {
                    const { isOBORequest, switchCustomerLink, accountLinks, signOutLink } = props;
                    if (isOBORequest) {
                        return (
                            <>
                                {switchCustomerLink}
                                {signOutLink}
                                {accountLinks ? <Node className='ms-profile-button-splitter' /> : false}
                                {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                            </>
                        );
                    } else {
                        return (
                            <>
                                {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                                {signOutLink}
                            </>
                        );
                    }
                };

                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>{_renderAccountDropdownLinks(props)}</Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    {props.logo}
                    <div className='desktop-navicon-container' ref={menuNode}>
                        {_renderReactFragment(props.menuBar)}
                    </div>
                    <div className='mobile-navicon-container'>{props.navIcon}</div>
                    {_renderMobileLogo()}
                    {props.config.staticlinks && props.config.staticlinks.length > 0 ? (
                        _renderStaticHeaderLinks(props.config.staticlinks)
                    ) : (
                        <></>
                    )}
                    {props.config.showContactSalesRep ? (
                        _renderSalesRepInformation(salesRepData, props)
                    ) : (
                        <></>
                    )}
                    {props.config.accountlinks && props.config.accountlinks.length > 0 ? (
                        _renderAccountHeaderLinks(props, props.config.accountlinks)
                    ) : (
                        <></>
                    )}
                    {_renderReactFragment(props.search)}
                    {props.preferredStore}
                    {props.currentDistributor}
                    {_renderAccountBlock(props)}
                    <Node {...Divider} />
                    {props.cartIcon}
                    {_renderReactFragment(props.siteOptions)}
                </Node>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {props.wishListIconMobile}
                            {props.signInLink}
                            {props.signOutLink}
                        </Node>
                    </Node>
                </Node>
            </Node>
        </Module>
    );
}


export default HeaderView;


